// Single source of truth for a scene's slide duration. Used by:
//   - Visor progress bar
//   - Visor gating timer (mandatoryType === "waitForTime")
//   - Editor ViewToolbar slideDuration input placeholder
//
// navItem.duration is frames at 30 fps, populated by
// _editor/components/timeline/IdeaTimeline/utils/buildLayers.js
// (aggregates box timeline endFrames, animations, sounds, exit animations).
//
// getEffectiveSlideDurationSec delegates to getSceneDurationSec (sceneTimeline.es6)
// when the scene has real content, then falls back to the existing floor chain.

import { FPS, getSceneDurationSec } from "./sceneTimeline";
export { FPS } from "./sceneTimeline";

function _toPositiveNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

// Auto-derived natural duration in seconds. 0 when no auto value is available.
export function getSceneNaturalDurationSec(sceneId, state) {
  if (!state || !sceneId) return 0;
  const navItem = state.navItemsById && state.navItemsById[sceneId];
  const frames = navItem && navItem.duration;
  if (typeof frames !== "number" || !(frames > 0)) return 0;
  return frames / FPS;
}

// Placeholder/default value shown when the user hasn't typed an override.
// Resolution: auto-computed → globalConfig.defaultSceneDuration → minTimeProgress floor → 3.
// Natural duration takes priority over the global default so the editor reflects
// the scene's actual animation length rather than a project-wide override.
export function getSuggestedSlideDurationSec(sceneId, state) {
  if (!state || !sceneId) return 3;
  const gc = state.globalConfig;
  const auto = getSceneNaturalDurationSec(sceneId, state);
  if (auto > 0) return auto;
  const gcDefault = gc && _toPositiveNumber(gc.defaultSceneDuration);
  if (gcDefault) return gcDefault;
  const floor = gc && _toPositiveNumber(gc.minTimeProgress);
  return floor || 3;
}

// Effective slide duration used to gate scene completion when mandatoryType
// is "waitForTime". Priority:
//   1. viewToolbar.slideDuration       — explicit user override
//   2. viewToolbar.minTime if > 3      — legacy override (3 is the editor's
//      auto-default, skip it so the suggested chain wins)
//   3. getSceneDurationSec (sceneTimeline) — content-derived: max of narration/box/video ends
//   4. getSuggestedSlideDurationSec(...) — global default / floor
//
// globalConfig.defaultSceneDuration is intentionally skipped before the content
// duration: the gating timer should match the scene's actual animation length,
// not a global default that overrides it.
export function getEffectiveSlideDurationSec(sceneId, state) {
  if (!state || !sceneId) return 3;
  const vt = state.viewToolbarsById && state.viewToolbarsById[sceneId];
  if (vt) {
    const sd = _toPositiveNumber(vt.slideDuration);
    if (sd) return sd;
    const mt = _toPositiveNumber(vt.minTime);
    if (mt && mt > 3) return mt;
  }
  const contentSec = getSceneDurationSec(sceneId, state);
  if (contentSec > 0) return contentSec;
  return getSuggestedSlideDurationSec(sceneId, state);
}

// Display duration for a scene row — same fallback chain as VisorPlayer's
// scrubber: content timeline → navItem.duration → longest sound endTime
// (when the first two are ≤ 1s). Shared by the visor scene list and the
// editor's Scenes header total so the two never disagree.
export function getSceneDisplayDurationSec(sceneId, state) {
  if (!state || !sceneId) return 0;
  let sec = getSceneDurationSec(sceneId, state);
  if (!(sec > 0)) sec = getSceneNaturalDurationSec(sceneId, state);
  if (!(sec > 1)) {
    const sounds = state.soundList && state.soundList[sceneId];
    if (Array.isArray(sounds) && sounds.length > 0) {
      sec = Math.max(...sounds.map((s) => s.endTime || 0));
    }
  }
  return sec > 0 ? sec : 0;
}

// "m:ss", or "h:mm:ss" past an hour.
export function formatDurationClock(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}
